import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { UserPlus } from 'lucide-react';
import { ROLES } from '../../constants/roles';
import toast from 'react-hot-toast';
import api from '../../lib/axios';
import './SignUpPage.css';

const SignUpPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: ROLES.USER,
    phone: '',
    address: '',
    zone: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [zones, setZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(true);
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Fetch available zones on component mount
  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoadingZones(true);
        const response = await api.get('/zones');
        if (response.data && response.data.data) {
          setZones(response.data.data || []);
        } else {
          setZones([]);
        }
      } catch (error) {
        console.error('Error fetching zones:', error);
        setZones([]);
        toast.error('Could not load zones. Please contact an administrator.');
      } finally {
        setLoadingZones(false);
      }
    };

    fetchZones();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Basic email and phone validation functions
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePhone = (phone) => {
    // Simple validation for 10-digit numbers
    const re = /^\d{10}$/;
    return re.test(String(phone));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- Start Validation ---
    if (!formData.name.trim()) {
      toast.error('Please enter your full name.');
      return;
    }
    if (!validateEmail(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    if (!validatePhone(formData.phone)) {
      toast.error('Please enter a valid 10-digit phone number.');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Please enter your address.');
      return;
    }
    if (!formData.zone) {
      toast.error('Please select a zone.');
      return;
    }
    // --- End Validation ---

    setIsLoading(true);
    try {
      // Exclude confirmPassword from the data sent to the backend
      const { confirmPassword, ...signupData } = formData;
      await signup(signupData);
      toast.success('Account created successfully! Redirecting to sign in...');
      // Redirect to sign-in page on successful signup
      setTimeout(() => navigate('/signin'), 2000);
    } catch (error) {
      console.error('Signup error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <UserPlus className="auth-icon" size={28} />
          <h1>Create Account</h1>
          <p>Join BinBuddy and help make a difference!</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create password (min. 6 characters)"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm password"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="role">Select Role</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
            >
              <option value={ROLES.USER}>User</option>
              <option value={ROLES.WORKER}>Worker</option>
              <option value={ROLES.VOLUNTEER}>Volunteer</option>
              {/* Admin role removed for security */}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter your 10-digit phone number"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="address">Address</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter your address"
              rows="3"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="zone">Zone</label>
            <select
              id="zone"
              name="zone"
              value={formData.zone}
              onChange={handleChange}
              required
              disabled={loadingZones || zones.length === 0}
            >
              <option value="">
                {loadingZones
                  ? 'Loading zones...'
                  : zones.length === 0
                    ? 'No zones available'
                    : 'Select your zone'
                }
              </option>
              {zones.map((zone) => (
                <option key={zone._id} value={zone._id}>
                  {zone.name}
                </option>
              ))}
            </select>
            {zones.length === 0 && !loadingZones && (
              <p className="form-help-text">
                No zones are available. Please contact an administrator.
              </p>
            )}
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading || loadingZones || zones.length === 0}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/signin" className="auth-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
